import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  User,
  Settings,
  CheckCircle,
  AlertTriangle,
  Play,
  Cloud
} from 'lucide-react';
// Old SpinningSankalvaXLogo import - commented out
// import SpinningSankalvaXLogo from '../UI/SpinningSankalvaXLogo';
// New Gruve Logo imports
import GruveLogo from '../UI/GruveLogo';
import SpinningGruveLogo from '../UI/SpinningGruveLogo';
import PreSalesOrderCreationModalSF from './PreSalesOrderCreationModalSF';

/**
 * Salesforce Sale Order V2 Chat Messages Component
 * Completely separate from NetSuite SaleV2ChatMessages
 * Uses Salesforce-specific APIs and PreSalesOrderCreationModalSF
 */
const SaleV2ChatMessagesSF = ({
  messages,
  isLoading,
  workflowName,
  error,
  runId,
  workflowId,
  token,
  sendMessage,
  executionStatus,
  currentStep,
  viewOnly = false,  // When true, don't show interactive modals (for viewing completed workflows)
  agentId = null     // Track source for cancel navigation (if from agent dashboard)
}) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [showSalesOrderModal, setShowSalesOrderModal] = useState(null);
  const [recentlyConfirmed, setRecentlyConfirmed] = useState(false);
  const processedMessageIdsRef = useRef(new Set());
  const hasInitializedRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [workflowContext, setWorkflowContext] = useState(null);

  // Fetch workflow context when modal is shown - Salesforce API
  useEffect(() => {
    if (showSalesOrderModal && runId && token) {
      const abortController = new AbortController();

      const statusUrl = `/api/sale-sf/workflows/status/${runId}`;
      console.log('🔵 SF: Fetching workflow context from:', statusUrl);

      fetch(statusUrl, {
        signal: abortController.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          console.log('🔵 SF: Full API response:', JSON.stringify(data, null, 2));

          // Try multiple paths to find run_context
          let runContext = null;

          if (data.success && data.data?.workflow_run?.run_context) {
            runContext = data.data.workflow_run.run_context;
            console.log('✅ SF: Found run_context at data.data.workflow_run.run_context');
          } else if (data.workflow_run?.run_context) {
            runContext = data.workflow_run.run_context;
            console.log('✅ SF: Found run_context at data.workflow_run.run_context');
          } else if (data.run_context) {
            runContext = data.run_context;
            console.log('✅ SF: Found run_context at data.run_context');
          } else if (data.data?.run_context) {
            runContext = data.data.run_context;
            console.log('✅ SF: Found run_context at data.data.run_context');
          }

          if (runContext) {
            console.log('✅ SF workflow context fetched:', runContext);
            console.log('📦 SF: product_mapping_ai_step_data:', runContext.product_mapping_ai_step_data);
            console.log('📦 SF: matched_items:', runContext.product_mapping_ai_step_data?.matched_items);
            setWorkflowContext(runContext);
          } else {
            console.warn('⚠️ SF: No run_context found in response. Response structure:', Object.keys(data));
            if (data.data) console.warn('⚠️ SF: data.data keys:', Object.keys(data.data));
          }
        })
        .catch(err => {
          if (err.name === 'AbortError') {
            console.log('🚫 SF workflow context fetch aborted');
          } else {
            console.error('❌ SF Error fetching workflow context:', err);
          }
        });

      return () => abortController.abort();
    }
  }, [showSalesOrderModal, runId, token]);

  console.log("🔵 SF SaleV2ChatMessagesSF mounted:", {
    messagesCount: messages?.length,
    isLoading,
    runId,
    executionStatus
  });

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Initialize processed messages on first mount - but DON'T add modal messages yet
  // so they can be processed for modal triggers first
  useEffect(() => {
    if (!hasInitializedRef.current && messages && messages.length > 0) {
      hasInitializedRef.current = true;
      messages.forEach(msg => {
        if (msg.id) {
          // Don't mark modal messages as processed during init - let modal logic handle them
          let hasModalComponent = false;
          try {
            let content = msg.content;
            if (typeof content === 'string') {
              const trimmed = content.trim();
              if (trimmed.startsWith('{')) {
                content = JSON.parse(trimmed);
              }
            }
            if (typeof content === 'object' && content?.component_definition?.metadata?.name === 'PreSalesOrderCreationModalSF') {
              hasModalComponent = true;
            }
          } catch (e) {}

          if (!hasModalComponent) {
            processedMessageIdsRef.current.add(msg.id);
          }
        }
      });
      console.log('🔵 SF: Initialized with', processedMessageIdsRef.current.size, 'processed messages (modal messages excluded)');
    }
  }, [messages]);

  // Process messages for Sales Order modal triggers
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    console.log('🔵 SF: Processing', messages.length, 'messages for modal triggers');

    for (const message of messages) {
      if (message.id && processedMessageIdsRef.current.has(message.id)) continue;
      if (message.id) processedMessageIdsRef.current.add(message.id);

      let parsedContent = null;
      try {
        if (typeof message.content === 'string') {
          const trimmed = message.content.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            parsedContent = JSON.parse(trimmed);
          }
        } else if (typeof message.content === 'object') {
          parsedContent = message.content;
        }
      } catch (e) {
        continue;
      }

      if (!parsedContent) continue;

      // Check for PreSalesOrderCreationModalSF component
      if (parsedContent.component_definition) {
        const componentDef = parsedContent.component_definition;
        console.log("🔵 SF FOUND UI COMPONENT:", componentDef);

        if (componentDef.metadata?.name === 'PreSalesOrderCreationModalSF') {
          console.log("🔵 SF FOUND Salesforce Sales Order Modal");

          // Don't show modal in view-only mode (viewing historical workflows)
          if (viewOnly) {
            console.log("🚫 SF viewOnly mode - NOT showing modal");
            continue;
          }

          if (executionStatus === 'completed') {
            console.log("🚫 SF workflow already completed - NOT showing modal");
            continue;
          }

          // Check if order was already created
          const orderAlreadyCreated = messages.some(msg => {
            try {
              let content = msg.content;
              if (typeof content === 'string') {
                return content.includes('Salesforce order created') ||
                       content.includes('Opportunity created') ||
                       content.includes('salesforce_order_created');
              }
              if (typeof content === 'object' && content !== null) {
                return content.next_action === 'salesforce_order_created';
              }
            } catch (e) {
              return false;
            }
            return false;
          });

          if (orderAlreadyCreated) {
            console.log("🚫 SF order already created - not reopening modal");
            continue;
          }

          if (!showSalesOrderModal && !recentlyConfirmed) {
            const salesOrderData = parsedContent.sales_order_data || componentDef;
            console.log("🔵 SF Opening modal with data:", salesOrderData);
            setShowSalesOrderModal({
              ...salesOrderData,
              runId: runId,
              workflowId: workflowId
            });
            return;
          }
        }
      }
    }
  }, [messages, executionStatus, runId, workflowId, showSalesOrderModal, recentlyConfirmed]);

  // Message type icons
  const getMessageIcon = (type) => {
    switch (type) {
      case 'user': return <User className="w-4 h-4" />;
      case 'assistant': return <Bot className="w-4 h-4" />;
      case 'system': return <Settings className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      case 'tool_call': return <Play className="w-4 h-4" />;
      case 'tool_result': return <CheckCircle className="w-4 h-4" />;
      default: return <Cloud className="w-4 h-4" />;
    }
  };

  const getMessageStyles = (type) => {
    switch (type) {
      case 'user':
        return {
          container: 'justify-end',
          bubble: 'bg-blue-600 text-white',
          icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
        };
      case 'assistant':
        return {
          container: 'justify-start',
          bubble: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700',
          icon: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400'
        };
      case 'system':
        return {
          container: 'justify-center',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700',
          icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
      case 'error':
        return {
          container: 'justify-start',
          bubble: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800',
          icon: 'bg-red-100 text-red-600'
        };
      case 'tool_call':
        return {
          container: 'justify-start',
          bubble: 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-800',
          icon: 'bg-orange-100 text-orange-600'
        };
      case 'tool_result':
        return {
          container: 'justify-start',
          bubble: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800',
          icon: 'bg-green-100 text-green-600'
        };
      default:
        return {
          container: 'justify-start',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
          icon: 'bg-gray-100 text-gray-600'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Render tool messages
  const renderToolMessage = (content) => {
    if (typeof content === 'string') {
      const lines = content.split('\n');
      const parts = [];
      let isJsonSection = false;
      let jsonContent = '';

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.startsWith('Parameters:') || line.startsWith('Result:')) {
          const colonIndex = line.indexOf(':');
          const label = line.substring(0, colonIndex + 1);
          const remainder = line.substring(colonIndex + 1).trim();

          parts.push(
            <div key={`label-${i}`} className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-2 mb-1">
              {label}
            </div>
          );

          isJsonSection = true;
          jsonContent = remainder || '';
          continue;
        }

        if (isJsonSection) {
          if (line.trim() === '' && jsonContent === '') continue;

          if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
            if (jsonContent.trim()) {
              let salesforceUrl = null;
              try {
                const parsedJson = JSON.parse(jsonContent.trim());
                salesforceUrl = parsedJson.salesforce_url || parsedJson.opportunity_url;
              } catch (e) {}

              parts.push(
                <div key={`json-${i}`} className="mt-1">
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-xs overflow-x-auto">
                    <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">{jsonContent.trim()}</pre>
                  </div>
                  {salesforceUrl && (
                    <div className="mt-2">
                      <a
                        href={salesforceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors"
                      >
                        <Cloud className="w-4 h-4 mr-2" />
                        View in Salesforce
                      </a>
                    </div>
                  )}
                </div>
              );
            }
            isJsonSection = false;
            jsonContent = '';
            parts.push(<div key={i} className="font-medium text-gray-900 dark:text-white">{line}</div>);
          } else {
            jsonContent += (jsonContent ? '\n' : '') + line;
          }
        } else if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
          parts.push(<div key={i} className="font-medium text-gray-900 dark:text-white">{line}</div>);
        } else if (line.trim()) {
          parts.push(<div key={i} className="text-gray-700 dark:text-gray-300">{line}</div>);
        }
      }

      if (isJsonSection && jsonContent.trim()) {
        parts.push(
          <div key="final-json" className="mt-1">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
              <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{jsonContent.trim()}</pre>
            </div>
          </div>
        );
      }

      return <div className="space-y-1">{parts}</div>;
    }

    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
        <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
          {typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content)}
        </pre>
      </div>
    );
  };

  const renderMessageContent = (content, type, message) => {
    if (type === 'tool_call' || type === 'tool_result') {
      return renderToolMessage(content);
    }

    // UI component messages
    if (message?.isUIComponent && typeof content === 'object' && content.component_definition) {
      return (
        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4">
          <div className="text-cyan-800 dark:text-cyan-200 font-medium">
            {content.message || 'Salesforce order review ready'}
          </div>
        </div>
      );
    }

    // Parse JSON content
    let parsedContent = content;
    if (typeof content === 'string') {
      let jsonStr = content.trim();
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/, '').replace(/```\s*$/, '').trim();
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/, '').replace(/```\s*$/, '').trim();
      }

      if (jsonStr.startsWith('{') || jsonStr.startsWith('[')) {
        try {
          parsedContent = JSON.parse(jsonStr);
        } catch (e) {}
      }
    }

    // Salesforce order creation success
    if (typeof parsedContent === 'object' && parsedContent !== null) {
      if ((parsedContent.next_action === 'order_created' || parsedContent.next_action === 'salesforce_order_created') && parsedContent.success === true) {
        const opportunityId = parsedContent.opportunity_id || parsedContent.order_id;
        const salesforceUrl = parsedContent.salesforce_url || parsedContent.opportunity_url;

        return (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-green-800 dark:text-green-200">
                  Salesforce Order Created!
                </h4>
                {opportunityId && (
                  <p className="text-green-700 dark:text-green-300 text-sm">
                    ID: <span className="font-mono font-bold">{opportunityId}</span>
                  </p>
                )}
              </div>
            </div>
            {parsedContent.message && (
              <p className="text-green-700 dark:text-green-300 mb-3">{parsedContent.message}</p>
            )}
            {salesforceUrl && (
              <a
                href={salesforceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors"
              >
                <Cloud className="w-4 h-4 mr-2" />
                View in Salesforce
              </a>
            )}
          </div>
        );
      }

      // Creation failed
      if (parsedContent.next_action === 'creation_failed' || (parsedContent.success === false && parsedContent.error)) {
        return (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-red-800 dark:text-red-200">
                  Salesforce Order Creation Failed
                </h4>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  {parsedContent.message || parsedContent.error || 'An error occurred'}
                </p>
              </div>
            </div>
          </div>
        );
      }

      // Hide intermediate messages
      const shownActions = ['order_created', 'salesforce_order_created', 'creation_failed'];
      if (parsedContent.next_action && !shownActions.includes(parsedContent.next_action)) {
        return null;
      }

      if (parsedContent.success === true && !parsedContent.opportunity_id && !parsedContent.order_id && !parsedContent.error) {
        return null;
      }

      if (parsedContent.message_for_user) {
        return <div className="whitespace-pre-wrap break-words">{parsedContent.message_for_user}</div>;
      }
      if (parsedContent.message) {
        return <div className="whitespace-pre-wrap break-words">{parsedContent.message}</div>;
      }

      if (parsedContent.component_definition || parsedContent.status === 'ui_ready') {
        return (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-blue-700 dark:text-blue-300">
              ☁️ {parsedContent.message || 'Salesforce order review ready'}
            </p>
          </div>
        );
      }

      return (
        <div className="whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-400">
          {parsedContent.message || parsedContent.status || JSON.stringify(parsedContent, null, 2)}
        </div>
      );
    }

    if (typeof content === 'string') {
      return <div className="whitespace-pre-wrap break-words">{content}</div>;
    }

    return null;
  };

  // Modal handlers
  const handleConfirmSalesOrder = async (finalSalesOrderData) => {
    try {
      console.log('🔵 SF: handleConfirmSalesOrder called with:', finalSalesOrderData);

      setShowSalesOrderModal(null);
      setRecentlyConfirmed(true);
      setIsProcessing(true);

      const userMessage = `Salesforce Order confirmed for creation with the following data: ${JSON.stringify({
        next_action_required: 'create_salesforce_order',
        sale_data: finalSalesOrderData
      })}`;

      console.log('🔵 SF: Sending order creation message');

      if (sendMessage) {
        await sendMessage(userMessage);
        console.log('✅ SF order creation message sent');
      } else {
        console.error('❌ sendMessage function not available');
      }

      setIsProcessing(false);
    } catch (err) {
      console.error('❌ SF Error confirming order:', err);
      setIsProcessing(false);
      setShowSalesOrderModal(null);
    }
  };

  const handleCancelSalesOrder = () => {
    console.log('🔵 SF order creation cancelled');
    setShowSalesOrderModal(null);
  };

  const handleCloseSalesOrder = () => {
    console.log('🔵 SF modal closed');
    setShowSalesOrderModal(null);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Error message */}
      {error && (
        <div className="flex justify-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-red-700 dark:text-red-200 max-w-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {messages && messages.map((message, index) => {
        const styles = getMessageStyles(message.type);

        if (message.isUIComponent) return null;

        const isToolMessage = message.type === 'tool_call' || message.type === 'tool_result';
        const maxWidthClass = isToolMessage ? 'max-w-[95%] lg:max-w-[90%]' : 'max-w-[90%] lg:max-w-[80%]';

        return (
          <div key={message.id || index} className={`flex ${styles.container}`}>
            <div className={`flex items-start gap-3 ${maxWidthClass} min-w-0`}>
              {message.type !== 'user' && (
                <div className={`p-2 rounded-full flex-shrink-0 ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}
              <div className={`rounded-lg px-4 py-3 shadow-sm min-w-0 flex-1 ${styles.bubble}`}>
                {renderMessageContent(message.content, message.type, message)}
                {message.timestamp && (
                  <div className="text-xs opacity-60 mt-2">{formatTimestamp(message.timestamp)}</div>
                )}
              </div>
              {message.type === 'user' && (
                <div className={`p-2 rounded-full ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Loading indicator */}
      {(isLoading || isProcessing) && (
        <div className="flex justify-start">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-cyan-100 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400">
              <Cloud className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700 shadow-sm">
              {/* Old: <SpinningSankalvaXLogo size="w-5 h-5" className="text-cyan-600 dark:text-cyan-400" showBackground={true} /> */}
              <SpinningGruveLogo size="w-5 h-5" className="text-cyan-600 dark:text-cyan-400" showBackground={true} />
            </div>
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Salesforce Sales Order Modal */}
      {showSalesOrderModal && (
        <PreSalesOrderCreationModalSF
          salesOrderData={showSalesOrderModal}
          onConfirm={handleConfirmSalesOrder}
          onCancel={handleCancelSalesOrder}
          onClose={handleCloseSalesOrder}
          token={token}
          workflowContext={workflowContext}
          runId={runId}
          workflowId={workflowId}
          agentId={agentId}
        />
      )}

      {console.log('🔵 SF: Modal state:', showSalesOrderModal ? 'OPEN' : 'closed', 'workflowContext:', workflowContext)}
    </div>
  );
};

export default SaleV2ChatMessagesSF;
